import React, { useEffect, useState } from 'react';
import { ENTER } from '../../../../utils/constants';
import { connect } from 'react-redux';
import { clearSuchvorschlaege } from '../../../../redux/actions/adresse';

interface TextAreaProps {
  label?: string;
  name?: string;
  classes?: string;
  maxLength?: number;
  maxLineBreaks?: number;
  onChangeHandler: (value: string) => void;
  parentFocusHandler?: (value: string) => void;
  value?: string;
  resetValue?: string;
  autocompleteField?: any;
  autocompleteFormat?: any;
  autocompleteHandler?: any;
  suchvorschlaege?: any;
  clearSuchvorschlaege?: any;
  readonly?: boolean;
}

// TODO: Read default values via CSS
const fontSizeInPixels = 16;
const lineHeight = 1.2;
const verticalPadding = 20.4;

const Textarea = ({
  label,
  name,
  classes = '',
  maxLength = 200,
  maxLineBreaks = 3,
  onChangeHandler,
  parentFocusHandler,
  value,
  resetValue = '',
  autocompleteField,
  autocompleteFormat,
  autocompleteHandler,
  suchvorschlaege,
  clearSuchvorschlaege,
  readonly = false,
}: TextAreaProps) => {
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(String(value));
  const [key, setKey] = useState<string>('');
  const [renderOnBlur, setRenderOnBlur] = useState(false);
  const [textAreaHeight, setTextAreaHeight] = useState('auto');
  const [parentHeight, setParentHeight] = useState('auto');
  const [showList, setShowList] = useState(false);

  const NEW_LINE_REGEXP = new RegExp(/\n/g);

  useEffect(() => {
    if (value === undefined) onChangeHandler(resetValue);
  }, [value]);

  useEffect(() => {
    if (renderOnBlur) {
      setTextAreaHeight(getMinHeight());
      setParentHeight(getMinHeight());
      setRenderOnBlur(false);
    } else {
      if (
        textAreaRef.current &&
        parseInt(parentHeight.split('px')[0]) <=
          textAreaRef.current.scrollHeight
      ) {
        setParentHeight(`${textAreaRef.current.scrollHeight}px`);
        setTextAreaHeight(`${textAreaRef.current.scrollHeight}px`);
      }
    }
  }, [text, renderOnBlur]);

  /**
   * Prevents triggering newline in the textarea when no other characters have been entered.
   * Updates the current KeyCode, which is then used by the change handler.
   *
   * @param event onKeyDown event
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ENTER && textEndsInEmptyLineBreak()) {
      event.preventDefault();
    }
    setKey(event.key);
  };

  /**
   * Updates the textarea value and textarea height when allowed to.
   *
   * @param event onChange event
   */
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (changeAllowed()) {
      const value = event.target.value;
      setText(value);
      onChangeHandler(value);
    }
    if (event.target.value.trim() === '' || event.target.value === undefined) {
      clearSuchvorschlaege();
    }
  };

  /**
   * Blur handler that trims the inserted text and sets the renderOnBlur field to true.
   */
  const handleBlur = () => {
    setText(text.trim());
    setRenderOnBlur(true);
  };

  /**
   * Determines if a change event is allowed within the textarea. Change is allowed if:
   *
   * a) the character entered is not a line break or;
   * b) the end of the textarea does not already contain an empty line break and
   * the maximum number of line breaks have not been reached
   *
   * @returns {boolean}
   */
  const changeAllowed = () =>
    key === ENTER
      ? !textEndsInEmptyLineBreak() && !hasMaxNumberOfLineBreaks()
      : true;

  const hasMaxNumberOfLineBreaks = () => {
    const lines = text.split(NEW_LINE_REGEXP);
    return lines.length >= maxLineBreaks;
  };

  const textEndsInEmptyLineBreak = () => {
    const lines = text.split(NEW_LINE_REGEXP);
    return lines[lines.length - 1] === '';
  };

  /**
   * The max height this textarea is allowed to have.
   *
   * @returns {string} height in pixels
   */
  const getMaxHeight = () => {
    return `${
      maxLineBreaks * lineHeight * fontSizeInPixels + verticalPadding
    }px`;
  };

  /**
   * The min height this textarea is allowed to have.
   *
   * @returns {string} height in pixels
   */
  const getMinHeight = () => {
    return `${
      lineHeight * fontSizeInPixels * text.split(NEW_LINE_REGEXP).length +
      verticalPadding
    }px`;
  };

  const onListClick = (suchvorschlag: { [x: string]: string }) => {
    autocompleteHandler
      ? autocompleteHandler(suchvorschlag)
      : onChangeHandler(suchvorschlag[autocompleteField]);
    setShowList(false);
    parentFocusHandler && parentFocusHandler('');
    clearSuchvorschlaege();
  };

  const handleBlurDiv = () => {
    clearSuchvorschlaege();
    setShowList(false);
  };

  const onFocusHandler = () => {
    setShowList(true);
    parentFocusHandler && name && parentFocusHandler(name);
    clearSuchvorschlaege();
  };

  return (
    <div
      className={`floating form-group ${classes}`}
      style={{ minHeight: parentHeight }}
      onBlur={handleBlurDiv}
    >
      <textarea
        placeholder=' '
        ref={textAreaRef}
        value={value}
        style={{
          height: textAreaHeight,
          maxHeight: `${getMaxHeight()}`,
          minHeight: `${getMinHeight()}`,
        }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        rows={1}
        maxLength={maxLength}
        onFocus={() => onFocusHandler()}
        readOnly={readonly}
      />
      <label className='floating-label'>{label}</label>
      {(autocompleteField || autocompleteFormat) &&
        showList &&
        suchvorschlaege &&
        suchvorschlaege.length > 0 && (
          <ul className='suchvorschlaege'>
            {suchvorschlaege.map((suchvorschlag: any, index: any) => {
              return (
                <li
                  className='lisuchvorschlaege'
                  key={index}
                  onMouseDown={() => onListClick(suchvorschlag)}
                >
                  {' '}
                  {autocompleteFormat
                    ? React.cloneElement(autocompleteFormat, {
                        suchvorschlag: suchvorschlag,
                      })
                    : suchvorschlag[autocompleteField]}{' '}
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
};

export default connect(null, { clearSuchvorschlaege })(Textarea);
